import React, { useState, useCallback } from 'react';
import { Download } from 'lucide-react';
import { useInventory } from '@/context/InventoryContext';
import { InlineMessage, Button } from '@/components/rc';
import { exportCellarCSV } from '@/utils/exportCellar';
import { openStripeCheckout } from '@/utils/stripeCheckout';
import { getPurchaseProvider } from '@/utils/purchaseAvailability';
import { purchasePremium } from '@/utils/playBilling';
import { CONFIG } from '@/constants';
import { authFetch } from '@/utils/authFetch';
import { FUNCTION_URLS } from '@/config/functionUrls';
import { trackEvent } from '@/config/analytics';

const DISMISS_KEY = 'rc-downgrade-banner-dismissed';

const DowngradeBanner: React.FC = () => {
  const { inventory, displayInventory, isOverFreeLimit, totalBottles } = useInventory();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  // See utils/purchaseAvailability. Export stays — it is the useful action for
  // a user whose bottles are hidden and who cannot upgrade from here.
  const provider = getPurchaseProvider();
  const canPurchase = provider !== null;

  const handleDismiss = useCallback(() => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  }, []);

  const handleExport = useCallback(() => {
    trackEvent('csv_exported', { source: 'downgrade_banner' });
    void exportCellarCSV(inventory);
  }, [inventory]);

  const handleUpgrade = useCallback(async () => {
    trackEvent('upgrade_initiated', { feature: 'downgrade_banner' });
    setUpgradeLoading(true);

    if (provider === 'play') {
      // Errors aren't surfaced here — this banner has no error slot, and a
      // failed/cancelled purchase just leaves the same "Upgrade" button
      // available to try again, same as the pre-existing catch-and-reset
      // behaviour below for Stripe.
      await purchasePremium();
      setUpgradeLoading(false);
      return;
    }

    try {
      const res = await authFetch(FUNCTION_URLS.createCheckout, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Failed to start checkout');
      const { url } = await res.json();
      if (url) await openStripeCheckout(url);
    } catch {
      setUpgradeLoading(false);
    }
  }, [provider]);

  if (!isOverFreeLimit || dismissed) return null;

  const displayBottles = displayInventory.reduce((sum, w) => sum + (Number(w.quantity) || 0), 0);
  const hiddenBottles = totalBottles - displayBottles;
  const hiddenWines = inventory.length - displayInventory.length;

  return (
    <div className="mb-4">
      <InlineMessage
        tone="warning"
        message={
          `${hiddenBottles} bottle${hiddenBottles === 1 ? '' : 's'} across ${hiddenWines} wine${hiddenWines === 1 ? '' : 's'} hidden. ` +
          `Free accounts show your most recent ${CONFIG.FREE_TIER.MAX_BOTTLES} bottles. ` +
          (canPurchase
            ? 'Upgrade to Rave Cave Reserve to see your full cellar.'
            : 'Rave Cave Reserve shows your full cellar.')
        }
      />
      <div className="flex gap-2 mt-2">
        {canPurchase && (
          <Button
            variantType="Primary"
            label={upgradeLoading ? (provider === 'play' ? 'Processing...' : 'Redirecting...') : 'Upgrade to Rave Cave Reserve'}
            onClick={handleUpgrade}
            disabled={upgradeLoading}
            className="text-xs"
          />
        )}
        <Button
          variantType="Secondary"
          label="Export Full Cellar"
          onClick={handleExport}
          className="text-xs"
        />
        <Button
          variantType="Secondary"
          label="Dismiss"
          onClick={handleDismiss}
          className="text-xs ml-auto"
        />
      </div>
    </div>
  );
};

export default DowngradeBanner;
